import {
  type Board,
  type Achievements,
  BOARD_HEIGHT,
  BOARD_WIDTH,
  type ChainBurst,
  type CellGravityAnimation,
  type CellGravityMove,
  type ColorComboBurst,
  type EraseBurst,
  type EraseTextAnimation,
  type FallingPair,
  type GameOverAnimation,
  type GameSettings,
  type LandingAnimation,
  type PairAnimation,
  type PairCell,
  type RuntimeSnapshot,
  type SceneState,
  createEmptyBoard,
  defaultAchievements,
  defaultSettings,
  pairCells
} from "../core/model";
import { createRandomPair, createRng, hashSeed, type Rng } from "../core/random";
import { type RankingEntry, createDefaultRanking, insertRanking, isRankedScore } from "../core/ranking";
import { activeTypeCount, applyErasedToLevel, fallIntervalMs, scoreForWave } from "../core/scoring";
import { applyColumnGravityWithMoves, canPlacePair, eraseMarked, findMatches, landingPair, lockPair, movePair, rotatePair } from "../core/rules";
import type { GameCommand } from "../platform/input";

export type RuntimeEvent = "move" | "rotate" | "drop" | "hit" | "pause" | "erase" | "gameOver" | "start";

const ERASE_TEXT_DURATION_MS = 1500;
const GAME_OVER_SEQUENCE_DURATION_MS = 1900;
const MAX_ACTIVE_ERASE_TEXT_ANIMATIONS = 8;

interface PendingErase {
  marked: boolean[][];
  count: number;
  frame: number;
  timer: number;
  seed: number;
  bursts: EraseBurst[];
  chainBurst: ChainBurst | null;
  colorBurst: ColorComboBurst | null;
}

export class GameRuntime {
  board: Board = createEmptyBoard();
  state: SceneState = "opening";
  score = 0;
  level = 1;
  levelProgress = 0;
  erasedTotal = 0;
  currentPair: FallingPair | null = null;
  nextPair: FallingPair | null = null;
  ranking: RankingEntry[];
  settings: GameSettings;
  achievements: Achievements;
  lastScoreAdd = 0;
  chain = 0;
  gameOverReason = "";
  pairAnimation: PairAnimation | null = null;
  gravityAnimation: CellGravityAnimation | null = null;
  landingAnimation: LandingAnimation | null = null;
  gameOverAnimation: GameOverAnimation | null = null;

  private rng: Rng = createRng(0x46524945);
  private fallTimer = 0;
  private pendingLandingCells: PairCell[] = [];
  private pendingErase: PendingErase | null = null;
  private eraseTextAnimations: EraseTextAnimation[] = [];
  private retainedGameOverBoard = false;
  private pendingNameEntryAfterGameOver = false;
  private events: RuntimeEvent[] = [];

  constructor(options: { ranking?: RankingEntry[]; settings?: GameSettings; achievements?: Achievements } = {}) {
    this.ranking = options.ranking ?? createDefaultRanking();
    this.settings = options.settings ?? defaultSettings();
    this.achievements = options.achievements ?? defaultAchievements();
  }

  start(seed = Date.now()): void {
    this.rng = createRng(seed);
    this.board = createEmptyBoard();
    this.state = "playing";
    this.score = 0;
    this.level = 1;
    this.levelProgress = 0;
    this.erasedTotal = 0;
    this.lastScoreAdd = 0;
    this.chain = 0;
    this.gameOverReason = "";
    this.pendingErase = null;
    this.eraseTextAnimations = [];
    this.gameOverAnimation = null;
    this.retainedGameOverBoard = false;
    this.pendingNameEntryAfterGameOver = false;
    this.pairAnimation = null;
    this.gravityAnimation = null;
    this.landingAnimation = null;
    this.pendingLandingCells = [];
    this.fallTimer = 0;
    this.nextPair = this.makePair();
    this.spawnPair();
    this.events.push("start");
  }

  update(dtMs: number): void {
    const dt = Math.min(1000, Math.max(0, dtMs));
    this.advanceEraseTextAnimations(dt);
    this.advanceLandingAnimation(dt);
    if (this.state === "gameOverSequence") {
      this.advanceGameOverSequence(dt);
      return;
    }
    if (this.advancePairAnimation(dt)) {
      return;
    }
    if (this.advanceGravityAnimation(dt)) {
      return;
    }

    if (this.state === "playing") {
      const interval = fallIntervalMs(this.level);
      this.fallTimer += dt;
      while (this.state === "playing" && this.fallTimer >= interval) {
        this.fallTimer -= interval;
        this.stepDownOrLock(false);
      }
      return;
    }

    if (this.state === "resolvingErase" && this.pendingErase) {
      this.pendingErase.timer += dt;
      while (this.pendingErase.timer >= 30 && this.pendingErase.frame < 16) {
        this.pendingErase.timer -= 30;
        this.pendingErase.frame += 1;
      }
      if (this.pendingErase.frame >= 16) {
        this.finishErase();
      }
      return;
    }

    if (this.state === "applyingGravity") {
      this.beginResolveOrSpawn();
    }
  }

  command(command: GameCommand): boolean {
    if (command === "help") {
      this.state = this.state === "help" ? "opening" : "help";
      return true;
    }

    if (this.state === "gameOverSequence") {
      return true;
    }

    if (this.state === "opening" || this.state === "ranking" || this.state === "gameOver") {
      if (
        command === "start" ||
        command === "confirm" ||
        command === "hardDrop" ||
        command === "rotateRight" ||
        command === "rotateLeft" ||
        command === "softDrop"
      ) {
        this.start();
        return true;
      } else if (command === "settings") {
        this.state = "settings";
        return true;
      }
      return true;
    }

    if (this.state === "settings") {
      if (command === "cancel" || command === "confirm") {
        this.state = "opening";
        return true;
      }
      return true;
    }

    if (this.state === "paused") {
      if (command === "cancel" || command === "confirm" || command === "start") {
        this.state = "playing";
        this.events.push("pause");
        return true;
      }
      return false;
    }

    if (this.state !== "playing" || !this.currentPair || this.pairAnimation) {
      return false;
    }

    if (command === "cancel") {
      this.state = "paused";
      this.events.push("pause");
      return true;
    }

    if (command === "left" || command === "right") {
      const dx = command === "left" ? -1 : 1;
      const moved = movePair(this.board, this.currentPair, dx, 0);
      if (moved) {
        this.animatePair(this.currentPair, moved, 70);
        this.events.push("move");
        return true;
      }
      return true;
    }

    if (command === "softDrop") {
      this.stepDownOrLock(false, true);
      return true;
    }

    if (command === "hardDrop") {
      this.hardDrop();
      return true;
    }

    if (command === "rotateRight" || command === "confirm") {
      const rotated = rotatePair(this.board, this.currentPair, -1);
      if (rotated) {
        this.animatePair(this.currentPair, rotated, 105);
        this.events.push("rotate");
        return true;
      }
      return true;
    }

    if (command === "rotateLeft") {
      const rotated = rotatePair(this.board, this.currentPair, 1);
      if (rotated) {
        this.animatePair(this.currentPair, rotated, 105);
        this.events.push("rotate");
        return true;
      }
      return true;
    }

    return false;
  }

  submitName(name: string): void {
    if (this.state !== "nameEntry") {
      return;
    }
    this.ranking = insertRanking(this.score, name, this.ranking);
    this.state = "opening";
    this.retainedGameOverBoard = true;
  }

  snapshot(): RuntimeSnapshot {
    return {
      board: this.board,
      state: this.state,
      score: this.score,
      level: this.level,
      levelProgress: this.levelProgress,
      erasedTotal: this.erasedTotal,
      currentPair: this.currentPair,
      nextPair: this.nextPair,
      pairAnimation: this.pairAnimation ? { ...this.pairAnimation, from: { ...this.pairAnimation.from }, to: { ...this.pairAnimation.to } } : null,
      gravityAnimation: this.gravityAnimation
        ? {
            moves: this.gravityAnimation.moves.map((move) => ({ ...move })),
            elapsed: this.gravityAnimation.elapsed,
            duration: this.gravityAnimation.duration
          }
        : null,
      landingAnimation: this.landingAnimation
        ? {
            cells: this.landingAnimation.cells.map((cell) => ({ ...cell })),
            elapsed: this.landingAnimation.elapsed,
            duration: this.landingAnimation.duration
          }
        : null,
      eraseAnimation: this.pendingErase
        ? {
            marked: this.pendingErase.marked,
            frame: this.pendingErase.frame,
            seed: this.pendingErase.seed,
            erased: this.pendingErase.count,
            chain: this.chain,
            bursts: this.pendingErase.bursts.map((burst) => ({ ...burst })),
            chainBurst: this.pendingErase.chainBurst ? { ...this.pendingErase.chainBurst } : null,
            colorBurst: this.pendingErase.colorBurst ? { ...this.pendingErase.colorBurst } : null
          }
        : null,
      eraseTextAnimations: this.eraseTextAnimations.map((animation) => ({
        bursts: animation.bursts.map((burst) => ({ ...burst })),
        chainBurst: animation.chainBurst ? { ...animation.chainBurst } : null,
        colorBurst: animation.colorBurst ? { ...animation.colorBurst } : null,
        elapsed: animation.elapsed,
        duration: animation.duration
      })),
      gameOverAnimation: this.gameOverAnimation ? { ...this.gameOverAnimation } : null,
      showAttractBoard: this.shouldShowAttractBoard(),
      lastScoreAdd: this.lastScoreAdd,
      chain: this.chain,
      gameOverReason: this.gameOverReason,
      settings: this.settings,
      achievements: { ...this.achievements }
    };
  }

  drainEvents(): RuntimeEvent[] {
    const drained = this.events;
    this.events = [];
    return drained;
  }

  private makePair(): FallingPair {
    return createRandomPair(this.rng, activeTypeCount(this.level), 2, 0);
  }

  private spawnPair(): void {
    const pair = this.nextPair ?? this.makePair();
    this.currentPair = { ...pair, x: 2, y: -1, orientation: 0 };
    this.nextPair = this.makePair();
    this.fallTimer = 0;

    if (!canPlacePair(this.board, this.currentPair)) {
      this.currentPair = null;
      this.endGame("No room for the next pair");
    }
  }

  private stepDownOrLock(emitMove = false, scoreManualDrop = false): void {
    if (!this.currentPair || this.state !== "playing") {
      return;
    }
    const dropped = movePair(this.board, this.currentPair, 0, 1);
    if (dropped) {
      this.animatePair(this.currentPair, dropped, 90);
      if (emitMove) {
        this.events.push("move");
      }
      if (scoreManualDrop) {
        this.addDropScore(1);
      }
      return;
    }
    this.lockCurrentPair();
  }

  private hardDrop(): void {
    if (!this.currentPair || this.state !== "playing") {
      return;
    }
    const from = this.currentPair;
    const to = landingPair(this.board, from);
    const distance = Math.max(0, to.y - from.y);
    this.addDropScore(distance);
    this.currentPair = to;
    this.pairAnimation = {
      from,
      to,
      elapsed: 0,
      duration: Math.min(260, 80 + distance * 24)
    };
    this.state = "dropping";
    this.events.push("drop");
  }

  private addDropScore(distance: number): void {
    const scoreAdd = Math.max(0, Math.floor(distance)) * this.level;
    if (scoreAdd <= 0) {
      return;
    }
    this.lastScoreAdd = scoreAdd;
    this.score += scoreAdd;
    this.achievements.highScore = Math.max(this.achievements.highScore, this.score);
  }

  private animatePair(from: FallingPair, to: FallingPair, duration: number): void {
    this.currentPair = to;
    this.pairAnimation = {
      from,
      to,
      elapsed: 0,
      duration
    };
  }

  private advancePairAnimation(dtMs: number): boolean {
    if (!this.pairAnimation) {
      return false;
    }
    this.pairAnimation.elapsed = Math.min(this.pairAnimation.duration, this.pairAnimation.elapsed + dtMs);
    if (this.pairAnimation.elapsed < this.pairAnimation.duration) {
      return true;
    }

    const wasDropping = this.state === "dropping";
    this.pairAnimation = null;
    if (wasDropping) {
      this.lockCurrentPair();
    }
    return true;
  }

  private advanceGravityAnimation(dtMs: number): boolean {
    if (!this.gravityAnimation) {
      return false;
    }
    this.gravityAnimation.elapsed = Math.min(this.gravityAnimation.duration, this.gravityAnimation.elapsed + dtMs);
    if (this.gravityAnimation.elapsed < this.gravityAnimation.duration) {
      return true;
    }

    this.gravityAnimation = null;
    if (this.pendingLandingCells.length > 0) {
      this.startLandingAnimation(this.pendingLandingCells);
      this.pendingLandingCells = [];
    }
    this.beginResolveOrSpawn();
    return true;
  }

  private advanceLandingAnimation(dtMs: number): void {
    if (!this.landingAnimation) {
      return;
    }
    this.landingAnimation.elapsed = Math.min(this.landingAnimation.duration, this.landingAnimation.elapsed + dtMs);
    if (this.landingAnimation.elapsed >= this.landingAnimation.duration) {
      this.landingAnimation = null;
    }
  }

  private lockCurrentPair(): void {
    if (!this.currentPair) {
      return;
    }
    const lockedCells = pairCells(this.currentPair).filter((cell) => cell.y >= 0);
    const gravity = applyColumnGravityWithMoves(lockPair(this.board, this.currentPair));
    this.board = gravity.board;
    this.currentPair = null;
    this.startGravityOrResolve(gravity.moves, lockedCells);
  }

  private startGravityOrResolve(moves: CellGravityMove[], fallbackLandingCells: PairCell[]): void {
    if (moves.length > 0) {
      const maxDistance = Math.max(...moves.map((move) => move.toY - move.fromY));
      this.pendingLandingCells = this.finalLandingCells(moves, fallbackLandingCells);
      this.gravityAnimation = {
        moves,
        elapsed: 0,
        duration: Math.min(300, 90 + maxDistance * 34)
      };
      this.state = "applyingGravity";
      return;
    }

    if (fallbackLandingCells.length > 0) {
      this.startLandingAnimation(fallbackLandingCells);
    }
    this.beginResolveOrSpawn();
  }

  private finalLandingCells(moves: CellGravityMove[], fallbackLandingCells: PairCell[]): PairCell[] {
    const cells = new Map<string, PairCell>();
    const movedBySource = new Map<string, PairCell>();
    for (const move of moves) {
      const finalCell = { x: move.toX, y: move.toY, type: move.type };
      movedBySource.set(`${move.fromX},${move.fromY},${move.type}`, finalCell);
      cells.set(`${finalCell.x},${finalCell.y},${finalCell.type}`, finalCell);
    }

    for (const cell of fallbackLandingCells) {
      const finalCell = movedBySource.get(`${cell.x},${cell.y},${cell.type}`) ?? cell;
      cells.set(`${finalCell.x},${finalCell.y},${finalCell.type}`, finalCell);
    }

    return [...cells.values()];
  }

  private startLandingAnimation(cells: PairCell[]): void {
    this.landingAnimation = {
      cells,
      elapsed: 0,
      duration: 280
    };
    this.events.push("hit");
  }

  private beginResolveOrSpawn(): void {
    const matches = findMatches(this.board, activeTypeCount(this.level));
    if (matches.count === 0) {
      this.chain = 0;
      this.state = "playing";
      this.spawnPair();
      return;
    }

    this.chain += 1;
    this.achievements.maxChain = Math.max(this.achievements.maxChain, this.chain);
    this.achievements.maxSimultaneousErase = Math.max(this.achievements.maxSimultaneousErase, matches.count);
    const center = this.markedCenter(matches.cells);
    const bursts = this.eraseBursts(matches.cells);
    const chainBurst: ChainBurst | null = this.chain >= 2 && center ? { chain: this.chain, x: center.x, y: center.y } : null;
    const colorBurst = this.colorComboBurst(matches.cells, center);
    this.pendingErase = {
      marked: matches.cells,
      count: matches.count,
      frame: 0,
      timer: 0,
      seed: hashSeed(this.score, this.level, this.chain, this.rng.state),
      bursts,
      chainBurst,
      colorBurst
    };
    this.startEraseTextAnimation(bursts, chainBurst, colorBurst);
    this.state = "resolvingErase";
    this.events.push("erase");
  }

  private startEraseTextAnimation(bursts: EraseBurst[], chainBurst: ChainBurst | null, colorBurst: ColorComboBurst | null): void {
    if (bursts.length === 0 && !chainBurst && !colorBurst) {
      return;
    }

    this.eraseTextAnimations.push({
      bursts: bursts.map((burst) => ({ ...burst })),
      chainBurst: chainBurst ? { ...chainBurst } : null,
      colorBurst: colorBurst ? { ...colorBurst } : null,
      elapsed: 0,
      duration: ERASE_TEXT_DURATION_MS
    });

    if (this.eraseTextAnimations.length > MAX_ACTIVE_ERASE_TEXT_ANIMATIONS) {
      this.eraseTextAnimations.splice(0, this.eraseTextAnimations.length - MAX_ACTIVE_ERASE_TEXT_ANIMATIONS);
    }
  }

  private advanceEraseTextAnimations(dtMs: number): void {
    if (this.eraseTextAnimations.length === 0) {
      return;
    }
    for (const animation of this.eraseTextAnimations) {
      animation.elapsed = Math.min(animation.duration, animation.elapsed + dtMs);
    }
    this.eraseTextAnimations = this.eraseTextAnimations.filter((animation) => animation.elapsed < animation.duration);
  }

  private advanceGameOverSequence(dtMs: number): void {
    if (!this.gameOverAnimation) {
      return;
    }

    this.gameOverAnimation.elapsed = Math.min(this.gameOverAnimation.duration, this.gameOverAnimation.elapsed + dtMs);
    if (this.gameOverAnimation.elapsed < this.gameOverAnimation.duration) {
      return;
    }

    this.gameOverAnimation = null;
    if (this.pendingNameEntryAfterGameOver) {
      this.pendingNameEntryAfterGameOver = false;
      this.state = "nameEntry";
      return;
    }
    this.state = "opening";
  }

  private shouldShowAttractBoard(): boolean {
    if (this.retainedGameOverBoard) {
      return false;
    }
    return this.state === "opening" || this.state === "ranking" || this.state === "help" || this.state === "settings";
  }

  private finishErase(): void {
    if (!this.pendingErase) {
      return;
    }

    const erased = this.pendingErase.count;
    this.lastScoreAdd = scoreForWave(this.level, erased, this.chain);
    this.score += this.lastScoreAdd;
    this.erasedTotal += erased;
    const levelResult = applyErasedToLevel(this.level, this.levelProgress, erased);
    this.level = levelResult.level;
    this.levelProgress = levelResult.progress;
    this.achievements.highScore = Math.max(this.achievements.highScore, this.score);
    this.achievements.maxLevel = Math.max(this.achievements.maxLevel, this.level);
    const gravity = applyColumnGravityWithMoves(eraseMarked(this.board, this.pendingErase.marked));
    this.board = gravity.board;
    this.pendingErase = null;
    this.startGravityOrResolve(gravity.moves, []);
  }

  private markedCenter(marked: boolean[][]): { x: number; y: number } | null {
    let xSum = 0;
    let ySum = 0;
    let count = 0;
    for (let y = 0; y < BOARD_HEIGHT; y += 1) {
      for (let x = 0; x < BOARD_WIDTH; x += 1) {
        if (marked[y][x]) {
          xSum += x + 0.5;
          ySum += y + 0.5;
          count += 1;
        }
      }
    }
    return count > 0 ? { x: xSum / count, y: ySum / count } : null;
  }

  private eraseBursts(marked: boolean[][]): EraseBurst[] {
    const groups = new Map<number, { count: number; x: number; y: number }>();
    for (let y = 0; y < BOARD_HEIGHT; y += 1) {
      for (let x = 0; x < BOARD_WIDTH; x += 1) {
        const type = this.board[y][x];
        if (!marked[y][x] || type === 0) {
          continue;
        }
        const group = groups.get(type) ?? { count: 0, x: 0, y: 0 };
        group.count += 1;
        group.x += x + 0.5;
        group.y += y + 0.5;
        groups.set(type, group);
      }
    }

    return [...groups.entries()]
      .filter(([, group]) => group.count >= 6)
      .map(([type, group]) => {
        const seed = hashSeed(this.score, this.level, this.chain, type, group.count, this.rng.state);
        return {
          type: type as EraseBurst["type"],
          count: group.count,
          x: group.x / group.count,
          y: group.y / group.count,
          angle: ((seed % 6283) / 1000) - Math.PI,
          distance: 1.15 + ((seed >>> 8) % 70) / 100
        };
      });
  }

  private colorComboBurst(marked: boolean[][], center: { x: number; y: number } | null): ColorComboBurst | null {
    if (!center) {
      return null;
    }

    const types = new Set<number>();
    for (let y = 0; y < BOARD_HEIGHT; y += 1) {
      for (let x = 0; x < BOARD_WIDTH; x += 1) {
        const type = this.board[y][x];
        if (marked[y][x] && type !== 0) {
          types.add(type);
        }
      }
    }

    if (types.size < 2) {
      return null;
    }

    const seed = hashSeed(this.score, this.level, this.chain, types.size, this.rng.state);
    return {
      colorCount: types.size,
      x: center.x,
      y: center.y,
      angle: ((seed % 6283) / 1000) - Math.PI
    };
  }

  private endGame(reason: string): void {
    this.gameOverReason = reason;
    this.retainedGameOverBoard = true;
    this.pendingNameEntryAfterGameOver = isRankedScore(this.score, this.ranking);
    this.gameOverAnimation = {
      elapsed: 0,
      duration: GAME_OVER_SEQUENCE_DURATION_MS
    };
    if (this.events[this.events.length - 1] === "hit") {
      this.events[this.events.length - 1] = "gameOver";
    } else {
      this.events.push("gameOver");
    }
    this.state = "gameOverSequence";
  }
}
